/**
 * MONSTER TOWN — BOOT. ARCHITECT-OWNED.
 *
 * The ONE ordered wiring point. Crews add exactly one `install*()` line each,
 * in their band, and nothing else. If your crew needs something to happen
 * earlier than its band, it does not — subscribe to an event instead.
 *
 * ORDER MATTERS AND IS DELIBERATE:
 *   1. content registered  (defs must exist before anything places one)
 *   2. renderer/camera/touch/world plumbing
 *   3. world crew registers scenes           <- everything else needs a scene
 *   4. build/monster/venue/ui crews install  <- they register save sections
 *   5. save.load()                           <- AFTER all sections registered
 *   6. engine.start()
 */

import './style.css'
import { engine } from './core/engine'
import { bus } from './core/events'
import { save } from './core/save'
import { wallet } from './core/economy'
import { world } from './core/scene'
import { registerSaveSection } from './core/save'
import type { WalletState } from './core/types'
import { GameRenderer } from './render/renderer'
import { IsoCamera } from './render/camera'
import { TouchRouter } from './input/touch'
import { app, type GameContext } from './app/context'
import { installRouter } from './app/router'
import { installInteractions } from './app/interactions'
import { installFirstRun } from './app/firstrun'
import { registerContent } from './content/index'
import { installClock } from './core/time'
import { installSchedule } from './core/schedule'
import { installDaySave, otherSlot } from './core/daySave'
import { installErrands, setCategoryHint } from './core/errands'
import { inventory } from './core/inventory'
import { collection, getCollectible } from './core/collection'
import { gifts } from './core/gifts'
import { getPlaceable } from './core/placeable'
import type { PlaceableId } from './core/types'
import { installWorld, townCenter, TOWN_SCENE_ID } from './world/town'
import { installBuild, selectPlaceable } from './build/placer'
import { installPlayer, teleportPlayer } from './monster/player'
import { installNpcs } from './npc/town-life'
import { installRoutines } from './routine/index'
import { installHud } from './ui/hud'
import { installErrandCrew } from './errands/index'
import { installAudio } from './audio/index'
import { installVenue } from './venue/index'
import { installZoo } from './venues/zoo/index'
import { installCooking } from './cooking/index'
import { installTimeOfDay } from './time/index'
import { installInteriors } from './interior/index'
import { installInteractionRuntime } from './interactions/index'
import { installVisit } from './visit/index'
import { installCatching } from './catching/index'
import { installVoice } from './voice/index'
import { installHarness } from './harness'
import { installPerf } from './perf/index'

function boot(): void {
  const canvas = document.getElementById('stage') as HTMLCanvasElement | null
  const hud = document.getElementById('hud')
  if (canvas === null || hud === null) throw new Error('boot: #stage or #hud missing')

  // 1. content
  registerContent()

  // 2. plumbing
  const renderer = new GameRenderer(canvas)
  const camera = new IsoCamera()
  const touch = new TouchRouter(canvas)
  const ctx: GameContext = { canvas, hud, renderer, camera, touch, world, engine }
  renderer.scene.add(world.root)

  const applyViewport = (): void => {
    renderer.resize(true)
    const s = renderer.cssSize()
    camera.setViewport(s.width, s.height)
  }
  applyViewport()
  window.addEventListener('resize', applyViewport)
  window.addEventListener('orientationchange', () => setTimeout(applyViewport, 120))

  installRouter(ctx)
  installInteractions()

  /**
   * "A DAY IN TOWN" CORE. Installed here, ahead of every crew, because the crews
   * ask it questions: what time is it, what is this neighbour doing, which
   * errand step is live, is that bug out right now.
   *
   * `installDaySave()` claims the seven new save sections and MUST run before
   * `save.load()` below — a section registered late gets defaults for ever.
   */
  installClock()
  installSchedule()
  installDaySave()
  /**
   * The errand engine reads the bag and the book to auto-satisfy a step the kid
   * has already done. Injected rather than imported so `core/errands.ts` stays
   * unit-testable without a world.
   */
  installErrands({
    countItem: (itemId) => inventory.count(itemId),
    countFound: (idOrGroup) => {
      const direct = collection.count(idOrGroup)
      if (direct > 0 || getCollectible(idOrGroup) !== undefined) return direct
      // A `group` step ('fish', 'bug') counts every distinct species found.
      return collection
        .foundIds()
        .filter((id) => getCollectible(id)?.kind === idOrGroup).length
    },
  })
  // Lets a `category` build step ('put three flowers in the garden') match
  // without the build crew emitting anything.
  setCategoryHint((defId) => getPlaceable(defId as PlaceableId)?.category ?? '')

  // The wallet is core state but nobody else owns it, so it saves from here.
  registerSaveSection<WalletState>(
    {
      id: 'wallet',
      version: 1,
      defaults: () => ({ coins: 60, lifetimeEarned: 60, tiers: [], owned: [] }),
      serialize: () => wallet.snapshot(),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        return {
          coins: Math.max(0, c.num(raw['coins'], c.defaults.coins)),
          lifetimeEarned: Math.max(0, c.num(raw['lifetimeEarned'], c.defaults.lifetimeEarned)),
          tiers: c.arr<string>(raw['tiers'], (e) => (typeof e === 'string' ? e : null)),
          owned: c.arr<never>(raw['owned'], (e) => (typeof e === 'string' ? (e as never) : null)),
        }
      },
    },
    (v) => wallet.restore(v),
  )

  // 3. world crew — scenes must exist before anything else touches them
  installWorld(ctx)

  // 4. crews (each registers its own save section here)
  installBuild(ctx)
  installPlayer(ctx)
  installHud(ctx)
  installInteriors(ctx)
  installInteractionRuntime(ctx)
  installVenue(ctx)
  /**
   * VENUE #3 REGISTERS **AFTER** THE VENUE CREW, AND THAT ORDER IS LOAD-BEARING.
   *
   * It was the other way round for one round, because the economy crew builds a
   * room for every venue in `allVenues()` at install time. That worked — and
   * quietly broke four QA steps, because `allVenues()` is read POSITIONALLY in
   * two places: `src/venue/harness.ts` frames `allVenues()[0]` as "the venue"
   * and `allVenues()[1]` as "the restaurant", and `harness.enterVenue()` with no
   * id takes the first registered `venue_*` scene. Registering the zoo first
   * silently pointed all of them at a room with no menu, so the ice cream shop's
   * own drive steps reported "0 waiting, served=0" — a venue crew failure caused
   * entirely by a newcomer taking index 0.
   *
   * The zoo therefore goes last and registers its own room (see `installZoo`);
   * `seedVenues()` still runs on `save:loaded`, so its starter kit arrives
   * through the venue crew's code exactly like the restaurant's.
   */
  installZoo(ctx)
  // The kitchen: recipes, the garden that grows their ingredients, and the
  // `make` observation the reference errand's third step waits for.
  installCooking(ctx)
  installNpcs(ctx)
  // Neighbours live their day, and the kid's monster gets its two gentle
  // invitations. AFTER the NPC crew: the roster has to exist to be given a day.
  installRoutines(ctx)
  /**
   * ERRANDS: the cards, the guide arrow, how an errand arrives, and neighbour
   * requests. AFTER the HUD (`installHud` calls `hud.replaceChildren()`, so a
   * layer added before it would be thrown away) and after the NPC crew, whose
   * `npc:requestGuide` wire is how a neighbour walks over and flags you down.
   */
  installErrandCrew(ctx)
  installAudio(ctx)
  /**
   * VISIT YOUR BROTHER'S TOWN. Installed after the HUD (it puts its own layer
   * inside `#hud`) and BEFORE `save.load()` below, so its `save:loaded` listener
   * restores this kid's album on the very first load.
   */
  installVisit(ctx)
  /**
   * FISHING, BUG CATCHING AND THE COLLECTION BOOK. After the HUD (the book's
   * button docks into the rail, which `hud.replaceChildren()` would otherwise
   * throw away) and after the world crew, which it asks over the bus where the
   * trees are. It registers no save section of its own — the book lives in the
   * architect's `collection` section and the bag in `inventory`.
   */
  installCatching(ctx)
  /**
   * SUNRISE, MIDDAY, GOLD EVENING, COSY NIGHT — and the bed fade in between.
   *
   * LAST of the crews on purpose. It replaces the world crew's static sky with
   * a canvas it repaints, and it dims every light it finds hanging off a scene,
   * so every scene and every light in the game has to already exist when it
   * looks. It registers no save section: the clock's `time` section (owned by
   * `daySave.ts`) is the entire state of the day.
   */
  installTimeOfDay(ctx)
  // Hands a brand new kid their own plot the moment their monster exists.
  installFirstRun(ctx)
  /**
   * THE VOICE. Recorded clips from the shared kids-arcade pipeline, same cast as
   * Monster Quest. LAST of the crews and deliberately the most powerless one in
   * the game: it subscribes to the bus, emits `audio:voice`, and can reach
   * nothing else. Everything it says is already a picture on the screen (LAW #4).
   */
  installVoice(ctx)

  /**
   * A GIFT ARRIVES BY BEING READ, NOT BY BEING WRITTEN.
   *
   * On every load the kid READS the other slot (read-only, cloned) and files
   * anything addressed to them into their OWN inbox. Nothing writes to the other
   * town, ever — see the header of `src/core/gifts.ts`. Skipped while visiting,
   * where live state belongs to somebody else.
   */
  bus.on('save:loaded', (e) => {
    if (save.visiting) return
    gifts.claimFrom(otherSlot(e.slot))
  })

  // 5. load AFTER every section is registered
  save.load(save.activeSlot)

  // 6. framing, then go
  const centre = townCenter()
  if (app.playerPos.x === 0 && app.playerPos.z === 0) teleportPlayer(centre)
  camera.lookAtPoint(app.playerPos, true)
  camera.setRotation(0, true)
  selectPlaceable('block_wood')

  engine.addSystem({
    id: 'render.frame',
    order: 95,
    render: (_alpha, frameMs) => {
      if (renderer.resize()) {
        const s = renderer.cssSize()
        camera.setViewport(s.width, s.height)
      }
      camera.update(frameMs)
      renderer.render(camera.camera, frameMs)
    },
  })

  // Pay every shader compile now, before the first frame a kid sees, so the
  // first camera rotation is not a stutter. See `GameRenderer.warmUp`.
  renderer.warmUp(camera.camera)

  installHarness(ctx)
  installPerf(ctx)
  app.setPhase('town')
  engine.start()

  bus.on('save:failed', (e) => console.error('[save] FAILED', e))
  if (world.activeSceneId === null) world.setActiveImmediate(TOWN_SCENE_ID)
}

/**
 * OFFLINE. See `src/sw/sw.js` for what the worker does and why.
 *
 * PRODUCTION ONLY. In dev a worker would sit in front of vite's module graph and
 * serve a kid's — sorry, a crew's — edits from cache; every tool in tools/ runs
 * against the dev server and would then be screenshotting yesterday's code.
 *
 * Registration is fire-and-forget and deliberately never blocks boot: an iPad
 * with the feature disabled, a private-mode context, or a host that fails to
 * serve sw.js all end up in the same place — the game runs, it just runs online.
 */
function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return
  if (!('serviceWorker' in navigator)) return
  const go = (): void => {
    navigator.serviceWorker
      .register('./sw.js')
      .catch((err: unknown) => console.warn('[sw] registration failed', err))
  }
  // Wait for load so the worker's precache pass never competes with the first
  // paint for bandwidth — but only if `load` has not already fired, because a
  // listener added after the event never runs at all.
  if (document.readyState === 'complete') go()
  else window.addEventListener('load', go, { once: true })
}

boot()
registerServiceWorker()
